import { useState } from 'react';
import { adminFetch } from '../lib/api';
import { slugify } from '../lib/slugify';
import { RichTextEditor } from './RichTextEditor';
import { PostStats } from './PostStats';
import type { PostDetail } from '@zen-bell/shared';

type Status = 'draft' | 'published' | 'hidden';

export function PostForm({
  post,
  onSaved,
  onCancel,
}: {
  post: PostDetail | null;
  onSaved: (status: Status) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(post?.title ?? '');
  const [slug, setSlug] = useState(post?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(Boolean(post));
  const [description, setDescription] = useState(post?.description ?? '');
  const [tags, setTags] = useState(post?.tags ?? '');
  const [coverImage, setCoverImage] = useState(post?.coverImage ?? '');
  const [content, setContent] = useState(post?.content ?? '');
  const [status, setStatus] = useState('');
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  const onTitleChange = (value: string) => {
    setTitle(value);
    if (!slugTouched) setSlug(slugify(value));
  };

  /** Uploads to the media volume and returns the public URL ('' on failure). */
  const uploadImage = async (file: File): Promise<string> => {
    // The API caps the body at 4MB; checking here avoids a wasted round trip.
    if (file.size > 4 * 1024 * 1024) {
      setStatus('Image is larger than 4MB');
      setError(true);
      return '';
    }
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
    try {
      setStatus('Uploading image...');
      setError(false);
      const { url } = await adminFetch<{ url: string }>('/blog/images', {
        method: 'POST',
        body: JSON.stringify({ filename: file.name, contentType: file.type, base64 }),
      });
      setStatus('');
      return url;
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Upload failed');
      setError(true);
      return '';
    }
  };

  const uploadCover = async (file: File) => {
    const url = await uploadImage(file);
    if (url) setCoverImage(url);
  };

  const save = async (nextStatus: Status) => {
    setBusy(true);
    setStatus(nextStatus === 'draft' ? 'Saving draft...' : 'Publishing...');
    setError(false);
    try {
      await adminFetch('/blog', {
        method: 'POST',
        body: JSON.stringify({
          id: post?.id,
          title,
          slug,
          description,
          content,
          tags,
          coverImage,
          status: nextStatus,
        }),
      });
      onSaved(nextStatus);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Save failed');
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div id="post-form-view">
      <button type="button" id="btn-back-to-list" className="btn-inline btn-ghost" onClick={onCancel}>
        &larr; Back to Posts
      </button>

      <div className="chart-container form-panel">
        <h3 id="create-post-header" className="section-title">
          {post ? 'Edit Post' : 'Create New Post'}
        </h3>

        <form
          id="blog-form"
          onSubmit={(e) => {
            e.preventDefault();
            void save('published');
          }}
        >
          <div className="blog-form-grid">
            <div className="blog-form-main">
              <div className="form-group">
                <label htmlFor="post-title">Title</label>
                <input id="post-title" value={title} onChange={(e) => onTitleChange(e.target.value)} required />
              </div>

              <div className="form-group">
                <label htmlFor="tiptap-editor">Content</label>
                {/* Rich text like the original admin. Stored as HTML; the API still
                    accepts markdown from older posts and renders whichever it gets. */}
                <RichTextEditor value={content} onChange={setContent} onUploadImage={uploadImage} />
              </div>
            </div>

            <div className="blog-form-sidebar">
              <div className="form-group">
                <label htmlFor="post-slug">URL Slug</label>
                <input
                  id="post-slug"
                  value={slug}
                  placeholder="e.g. my-first-post"
                  onChange={(e) => {
                    setSlugTouched(true);
                    setSlug(slugify(e.target.value));
                  }}
                  pattern="[a-z0-9]+(-[a-z0-9]+)*"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="post-desc">SEO Description (Optional)</label>
                <input id="post-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>

              <div className="form-group">
                <label htmlFor="post-tags">Tags (Comma separated)</label>
                <input
                  id="post-tags"
                  value={tags}
                  placeholder="Mindfulness, Meditation, Tips"
                  onChange={(e) => setTags(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="post-cover">Cover Image (Optional)</label>
                <div className="file-upload-wrapper">
                  <input
                    id="post-cover"
                    value={coverImage}
                    placeholder="Image URL or upload..."
                    onChange={(e) => setCoverImage(e.target.value)}
                  />
                  {/* The real input is hidden by admin.css; the label is the button. */}
                  <label className="file-upload-btn" htmlFor="cover-upload">Upload</label>
                  <input
                    id="cover-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (file) void uploadCover(file);
                    }}
                  />
                </div>
                {coverImage ? (
                  <img id="cover-preview-img" className="cover-preview" src={coverImage} alt="Cover preview" />
                ) : null}
              </div>

              <div className="form-actions">
                <span id="blog-status" className={error ? 'form-error' : undefined}>{status}</span>
                <button id="btn-draft-post" type="button" className="btn-outline" disabled={busy} onClick={() => void save('draft')}>
                  Save as Draft
                </button>
                <button id="btn-submit-post" type="submit" disabled={busy}>
                  {post?.status === 'published' ? 'Update Post' : 'Publish Post'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
      {post ? <PostStats postId={post.id} /> : null}
    </div>
  );
}
